/**
 * GenerationModal — AI image generation workflow with intelligent context.
 * Mirrors Tauri IntelligentGenerationModal for feature parity.
 */
import { useState, useMemo } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useAppStore } from '@/store/appStore';
import { useCanvasStore } from '@/store/canvasStore';
import { Sparkles, X, Bot, Image as ImageIcon, Wand2, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GenerationConfig {
  maxAttempts: number;
  useVisionValidation: boolean;
  autoAddToCanvas: boolean;
}

export function GenerationModal() {
  const { showGenerationModal, setShowGenerationModal, currentProjectId } = useAppStore();
  const accountId = useAppStore((s) => s.currentAccountId) ?? undefined;
  const [prompt, setPrompt] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<'input' | 'analyzing' | 'generating'>('input');
  const [config, setConfig] = useState<GenerationConfig>({
    maxAttempts: 3,
    useVisionValidation: true,
    autoAddToCanvas: true,
  });
  
  const { addLayer } = useCanvasStore();

  const stylePresets = useQuery(api.stylePresets.list, { accountId });
  const entities = useQuery(api.entities.list, { accountId });
  const assets = useQuery(api.assets.list, { accountId });
  const createRun = useMutation(api.generation.create);
  const runs = useQuery(
    api.generation.listByProject,
    currentProjectId ? { accountId, project_id: currentProjectId } : 'skip'
  );

  // Build context summary for display
  const contextSummary = useMemo(() => {
    if (!entities && !assets) return null;
    return {
      entities: entities?.length ?? 0,
      assets: assets?.length ?? 0,
      selectedEntities: entities?.filter((e) => 
        ['artist', 'venue', 'promoter'].includes(e.entityType)
      ).length ?? 0,
    };
  }, [entities, assets]);

  const handleGenerate = async () => {
    if (!prompt.trim() || !currentProjectId) return;
    
    setStep('analyzing');
    setIsSubmitting(true);
    
    // Simulate analysis step
    await new Promise((resolve) => setTimeout(resolve, 800));
    
    setStep('generating');
    
    try {
      await createRun({
        accountId,
        project_id: currentProjectId,
        mode: 'background_first',
        prompt_payload: {
          prompt: prompt.trim(),
          width: 1080,
          height: 1350,
          style_preset_id: selectedPresetId || undefined,
          // Include context
          context: {
            entities: entities?.map((e) => ({
              id: e._id,
              name: e.name,
              type: e.entityType,
            })),
            assets: assets?.map((a) => ({
              id: a._id,
              name: a.name,
              type: a.type,
            })),
          },
        },
        status: 'pending',
      });
      
      setPrompt('');
      setStep('input');
      setShowGenerationModal(false);
    } catch (err) {
      console.error('Create generation run failed:', err);
      setStep('input');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showGenerationModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-lg rounded-xl border border-border bg-card p-6 shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold flex items-center gap-2">
            {step === 'input' && <><Sparkles className="h-5 w-5 text-primary" /> Generate</>}
            {step === 'analyzing' && <><Bot className="h-5 w-5 text-primary" /> Analyzing Context</>}
            {step === 'generating' && <><Loader2 className="h-5 w-5 text-primary animate-spin" /> Generating</>}
          </h2>
          <button
            onClick={() => {
              setShowGenerationModal(false);
              setStep('input');
            }}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {step === 'input' && (
          <div className="space-y-4">
            {/* Context Summary */}
            {contextSummary && (
              <div className="rounded-lg bg-muted/50 p-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <Bot className="h-4 w-4" />
                  <span>AI will use context from:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 text-xs bg-background rounded-full px-2 py-1">
                    {contextSummary.entities} entities
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs bg-background rounded-full px-2 py-1">
                    {contextSummary.assets} assets
                  </span>
                </div>
              </div>
            )}

            {/* Prompt Input */}
            <div>
              <label className="text-sm font-medium block mb-1">What would you like to generate?</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe the flyer background you want..."
                rows={3}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>

            {/* Style Preset */}
            <div>
              <label className="text-sm font-medium block mb-1">Style preset</label>
              <select
                value={selectedPresetId}
                onChange={(e) => setSelectedPresetId(e.target.value)}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">None (auto-detect)</option>
                {stylePresets?.map((p) => (
                  <option key={p._id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Options */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.useVisionValidation}
                  onChange={(e) => setConfig((c) => ({ ...c, useVisionValidation: e.target.checked }))}
                  className="rounded border-input"
                />
                <span>Use vision validation</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.autoAddToCanvas}
                  onChange={(e) => setConfig((c) => ({ ...c, autoAddToCanvas: e.target.checked }))}
                  className="rounded border-input"
                />
                <span>Auto-add to canvas when complete</span>
              </label>
            </div>

            {!currentProjectId && (
              <p className="text-xs text-amber-600">Open or create a project first.</p>
            )}

            {/* Recent Runs */}
            {runs && runs.length > 0 && (
              <div className="border-t border-border pt-4">
                <p className="text-xs font-medium text-muted-foreground mb-2">Recent runs</p>
                <div className="space-y-1 max-h-24 overflow-auto">
                  {runs.slice(0, 5).map((r) => (
                    <div key={r.id} className="text-xs flex items-center justify-between gap-2 py-1">
                      <span className="truncate">{r.status} · {(r.prompt_payload as { prompt?: string })?.prompt?.slice(0, 30) ?? '—'}…</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'analyzing' && (
          <div className="py-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Bot className="h-8 w-8 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">Analyzing your project context...</p>
            <p className="text-xs text-muted-foreground mt-1">Selecting relevant entities and assets</p>
          </div>
        )}

        {step === 'generating' && (
          <div className="py-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
            </div>
            <p className="text-sm text-muted-foreground">Generating your image...</p>
            <p className="text-xs text-muted-foreground mt-1">This may take a minute</p>
          </div>
        )}

        {/* Footer */}
        {step === 'input' && (
          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={() => setShowGenerationModal(false)}
              className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={!prompt.trim() || !currentProjectId || isSubmitting}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Processing...</>
              ) : (
                <><Wand2 className="h-4 w-4" /> Generate</>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
